import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material'
import { type KeyboardEvent, useState } from 'react'
import { type ChangeHandler, useForm } from 'react-hook-form'

import { Box, Modal } from '@/components/ui'
import {
  type GenericFormText,
  INTAKE_FORM_NAME_MAX_LENGTH,
  INTAKE_FORM_TEXT_MAX_LENGTH,
} from '@/definitions/intakeForm'
import { useGenericFormText } from '@/hooks/useGenericFormText'
import { useSnackbar } from '@/hooks/useSnackbar'

import {
  type EditGenericFormValues,
  useEditGenericForm,
} from '../../hooks/useEditGenericForm'
import { describeApiFailure } from '../../utils/describeApiFailure'
import { isFormGone } from '../../utils/isFormGone'

const MODAL_TITLE = 'Edit Generic Form Text'

const TITLE_REQUIRED_MESSAGE = 'Give the form a title.'
const TITLE_TOO_LONG_MESSAGE =
  `A title can be at most ${INTAKE_FORM_NAME_MAX_LENGTH} characters.`
const INTRO_REQUIRED_MESSAGE = 'Enter the introductory text.'
const COMPLETION_REQUIRED_MESSAGE = 'Enter the completion text.'
const TEXT_TOO_LONG_MESSAGE =
  `This can be at most ${INTAKE_FORM_TEXT_MAX_LENGTH} characters.`

const TITLE_HELPER_TEXT =
  'Shown at the top of the form, and used as the form’s name on this list.'
const INTRO_HELPER_TEXT = 'What a patient reads before starting the form.'
const COMPLETION_HELPER_TEXT = 'What a patient sees after submitting the form.'

const TEXT_UNREADABLE =
  'This form’s current text could not be loaded, so there is nothing to edit from'

const FORM_GONE_MESSAGE = 'This form is no longer there. Close the box and reload the list.'

const UNCHANGED_NOTICE =
  'The questions on the form are not changed by this, and the patient link ' +
  'already in use keeps working.'

// A deactivated form is served to nobody: the patient-facing path filters on the
// same `enabled` column the row renders as a red chip, and 404s. The edit is
// still allowed — the API carries the state forward, and this is the only screen
// that can reach a withdrawn form — so the copy changes rather than the action.
const DEACTIVATED_NOTICE =
  'The questions on the form are not changed by this. This form is deactivated, ' +
  'so its patient link serves nothing until it is switched back on — the edit is ' +
  'stored either way.'

const TEXT_INCOMPLETE_MESSAGE =
  'Part of this form’s text could not be read from its stored definition, so an ' +
  'empty box below may not match what a patient currently sees. Saving replaces ' +
  'that part of the definition with what you type.'

const NO_LINE_BREAKS_MESSAGE =
  'The form renders this as a single line, so a line break cannot be saved.'

// The intro and completion fields are multiline only so long prose wraps: the
// form renders each as one line, so a line break is kept out rather than refused.
const refuseEnter = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    event.preventDefault()
  }
}

const withoutLineBreaks =
  (onChange: ChangeHandler): ChangeHandler =>
    (event) => {
      if (/[\r\n]/.test(event.target.value)) {
        event.target.value = event.target.value.replace(/\s*[\r\n]+\s*/g, ' ')
      }
      return onChange(event)
    }

interface EditGenericFormModalProps {
  orgId: string
  baseFormId: string
  /** Whether patients can currently open the form, so the copy can say so. */
  enabled: boolean
  onClose: () => void
}

interface EditGenericFormBoxProps extends EditGenericFormModalProps {
  /** The current text, which the fields open holding. */
  currentText: GenericFormText
}

/**
 * The three text fields, once the values they start from are known.
 *
 * Split from the component below so `defaultValues` is right on the first render
 * that shows a field: react-hook-form reads them once, and resetting the box
 * from a later arrival would discard whatever the operator had already typed.
 */
const EditGenericFormBox = ({
  orgId,
  baseFormId,
  enabled,
  onClose,
  currentText,
}: EditGenericFormBoxProps) => {
  const { showSuccess } = useSnackbar()

  // Latched with the snapshot `defaultValues` took, so a refetch of `currentText`
  // doesn't make every untouched field compare as changed.
  const [openedText] = useState(currentText)

  const openedWith: EditGenericFormValues = {
    title: openedText.title,
    intro_text: openedText.intro_text,
    completion_text: openedText.completion_text,
  }

  // The API returns "" for a value it cannot read from the stored definition, so
  // an empty box may hide content that saving would replace.
  const isIncomplete = Object.values(openedWith).some((value) => value === '')

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<EditGenericFormValues>({ defaultValues: openedWith })

  const edit = useEditGenericForm(orgId, baseFormId, openedText, {
    onTitleRefused: (message) => setError('title', { message }),
    onFieldsRefused: (byField) => {
      for (const [field, message] of Object.entries(byField)) {
        setError(field as keyof EditGenericFormValues, { message })
      }
    },
    // `saved.enabled` rather than the prop: the prop is the row as it was when
    // Edit was pressed, and the form can have been withdrawn since. The response
    // carries the state the new revision was actually written with.
    onSaved: (saved) => {
      showSuccess(
        saved.enabled
          ? `${saved.display_name} was updated. Patients see the new text now.`
          : `${saved.display_name} was updated. It stays deactivated, so no patient sees it yet.`
      )
      onClose()
    },
  })

  const withRetiredProblem =
    (onChange: ChangeHandler): ChangeHandler =>
      (event) => {
        edit.retireProblem()
        return onChange(event)
      }

  // Mirrors the API: length and line breaks apply only to a changed field, so a
  // stored over-long value can't block an edit elsewhere. The trimmed value is
  // judged, since that is what the save sends, and counted in code points as
  // Python's len() counts it.
  const boundsOf = (
    field: keyof EditGenericFormValues,
    limit: number,
    tooLong: string
  ) => ({
    maxLength: (value: string) =>
      value === openedWith[field] || [...value.trim()].length <= limit || tooLong,
    singleLine: (value: string) =>
      value === openedWith[field] ||
      !/[\r\n]/.test(value.trim()) ||
      NO_LINE_BREAKS_MESSAGE,
  })

  // `required` fails only on the empty string, so text of spaces alone would
  // otherwise reach the API and come back as a refusal about the whole box.
  const titleField = register('title', {
    validate: {
      required: (value) => value.trim() !== '' || TITLE_REQUIRED_MESSAGE,
      ...boundsOf('title', INTAKE_FORM_NAME_MAX_LENGTH, TITLE_TOO_LONG_MESSAGE),
    },
  })
  const introField = register('intro_text', {
    validate: {
      required: (value) => value.trim() !== '' || INTRO_REQUIRED_MESSAGE,
      ...boundsOf('intro_text', INTAKE_FORM_TEXT_MAX_LENGTH, TEXT_TOO_LONG_MESSAGE),
    },
  })
  const completionField = register('completion_text', {
    validate: {
      required: (value) => value.trim() !== '' || COMPLETION_REQUIRED_MESSAGE,
      ...boundsOf(
        'completion_text',
        INTAKE_FORM_TEXT_MAX_LENGTH,
        TEXT_TOO_LONG_MESSAGE
      ),
    },
  })

  return (
    <Modal
      title={MODAL_TITLE}
      onClose={onClose}
      disableClose={edit.isSaving}
      maxWidth='sm'
      actions={
        <>
          <Button onClick={onClose} disabled={edit.isSaving}>
            Cancel
          </Button>
          <Button
            type='submit'
            form='edit-generic-form'
            variant='contained'
            disabled={edit.isSaving}
          >
            Save
          </Button>
        </>
      }
    >
      <Box
        component='form'
        id='edit-generic-form'
        variant='flexColumn'
        gap='md'
        onSubmit={handleSubmit(edit.submit)}
      >
        <TextField
          {...titleField}
          onChange={withRetiredProblem(titleField.onChange)}
          label='Title'
          fullWidth
          error={Boolean(errors.title)}
          helperText={errors.title?.message ?? TITLE_HELPER_TEXT}
          disabled={edit.isSaving}
        />

        <TextField
          {...introField}
          onChange={withRetiredProblem(withoutLineBreaks(introField.onChange))}
          onKeyDown={refuseEnter}
          label='Introductory text'
          fullWidth
          multiline
          minRows={3}
          error={Boolean(errors.intro_text)}
          helperText={errors.intro_text?.message ?? INTRO_HELPER_TEXT}
          disabled={edit.isSaving}
        />

        <TextField
          {...completionField}
          onChange={withRetiredProblem(withoutLineBreaks(completionField.onChange))}
          onKeyDown={refuseEnter}
          label='Completion text'
          fullWidth
          multiline
          minRows={3}
          error={Boolean(errors.completion_text)}
          helperText={errors.completion_text?.message ?? COMPLETION_HELPER_TEXT}
          disabled={edit.isSaving}
        />

        {isIncomplete && <Alert severity='warning'>{TEXT_INCOMPLETE_MESSAGE}</Alert>}

        <Alert severity='info'>
          {enabled ? UNCHANGED_NOTICE : DEACTIVATED_NOTICE}
        </Alert>

        {edit.problem !== null && (
          <Alert severity='error'>
            {edit.reasons.length === 0 ? (
              edit.problem
            ) : (
              <>
                <AlertTitle>{edit.problem}</AlertTitle>
                {/* One entry per finding, as the API sends them — a joined
                    sentence would leave the operator parsing prose. */}
                <Box variant='flexColumn' gap='xs'>
                  {edit.reasons.map((reason) => (
                    <Typography key={reason} variant='body2'>
                      {reason}
                    </Typography>
                  ))}
                </Box>
              </>
            )}
          </Alert>
        )}
      </Box>
    </Modal>
  )
}

/**
 * Changes the title, introductory text and completion text of an organization's
 * generic intake form.
 *
 * The current values are fetched rather than read off the forms list, which does
 * not carry them. A fetch that fails leaves no box rather than an empty one.
 *
 * A value the API could not read out of the stored definition comes back as "",
 * which is a usable box rather than a failure — so the box opens and says which
 * part it could not read, instead of pretending the form's text is empty.
 */
export const EditGenericFormModal = ({
  orgId,
  baseFormId,
  enabled,
  onClose,
}: EditGenericFormModalProps) => {
  const currentText = useGenericFormText(orgId, baseFormId)

  // Latched rather than read live: returning to the loading branch would unmount
  // the box and take the operator's edits with it. A paused query counts as
  // settled, or an offline fetch that is never sent holds the spinner forever.
  const [hasTextSettled, setHasTextSettled] = useState(false)
  if (!hasTextSettled && (!currentText.isPending || currentText.isPaused)) {
    setHasTextSettled(true)
  }

  const shell = (children: React.ReactNode) => (
    <Modal
      title={MODAL_TITLE}
      onClose={onClose}
      maxWidth='sm'
      actions={<Button onClick={onClose}>Close</Button>}
    >
      {children}
    </Modal>
  )

  if (!hasTextSettled) {
    return shell(
      <Box variant='flexCenter'>
        <CircularProgress />
      </Box>
    )
  }

  // Judged on whether there is text to edit rather than on the request having
  // succeeded: a 200 carrying nothing would otherwise open a box of blank fields
  // that overwrite the live text on save.
  if (currentText.data === undefined) {
    // The global toast is suppressed, so this is the only place the cause is shown.
    return shell(
      <Alert severity='error'>
        {isFormGone(currentText.error)
          ? FORM_GONE_MESSAGE
          : describeApiFailure(currentText.error, TEXT_UNREADABLE)}
      </Alert>
    )
  }

  return (
    <EditGenericFormBox
      orgId={orgId}
      baseFormId={baseFormId}
      enabled={enabled}
      onClose={onClose}
      currentText={currentText.data}
    />
  )
}
